import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  useLoading,
  useModal,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { ONLY_DATE_FORMAT } from '../../../constants/app';
import { FLEET_SERVICE_STATUS_LABELS } from '../../../constants/fleet';
import { TABLE_COLUMNS_NAME } from '../../../constants/fleetServices';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { apiParamsState } from '../../../storage/app';
import { FleetJobType, FleetService } from '../../../types/fleet';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import { StyledTooltip } from '../../ui/StyledTooltip';
import { useFleetDeleteService } from '../hooks/useFleetDeleteService';
import { useFleetServices } from '../hooks/useFleetServices';
import FleetServiceJobModal from './jobs/FleetServiceJobModal';

export function FleetServiceList() {
  const { theme, css, classes } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [services, setServices] = useState<FleetService[]>();
  const {
    page,
    onChangePage,
    loadFleetServices,
    reload,
    search,
    filters: { configurationId, status },
    onReload,
  } = useFleetServices();
  const [totalItems, setTotalItems] = useState(0);
  const { formatDateFromTs } = useMoment();
  const { loading, stopLoading, startLoading } = useLoading(true);
  const { showFailFeedback } = useFeedback();
  const { onOpenDeleteModal, deleteServiceDisplay } = useFleetDeleteService(onReload);
  const [selectedService, setSelectedService] = useState<FleetService>();
  const { showModal, closeModal, openModal } = useModal();

  useUpdateEffect(() => {
    onChangePage(0);
  }, [search, reload, configurationId, status, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (page === 0) {
      onChangePage(1);
      return;
    }
    const loadData = async () => {
      startLoading();
      await loadServices();
      stopLoading();
    };

    loadData();
  }, [page]);

  const loadServices = async () => {
    const { services: fleetServices, error, total } = await loadFleetServices();
    if (error) {
      showFailFeedback(error);
      setServices(undefined);
      setTotalItems(0);
    } else {
      setServices(fleetServices);
      setTotalItems(total ?? 0);
    }
  };

  const onOpenServiceJobModal = (service: FleetService) => {
    setSelectedService(service);
    openModal();
  };

  const onCloseServiceJobModal = () => {
    closeModal();
    setSelectedService(undefined);
  };

  const formatOnlyDateFromTs = (ts: number) => {
    return formatDateFromTs(ts, ONLY_DATE_FORMAT);
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!services && !loading && <EmptyState title="Todavía no cuentas con activos." />}
      {!services && loading && <Loading />}
      {services && (
        <>
          {!!services?.length && (
            <StyledPaginatedTable
              columns={TABLE_COLUMNS_NAME}
              minColumnWidth="300px"
              data={services.map((service) => {
                return {
                  items: [
                    service?.asset.vehicle?.licensePlate || service?.asset.container?.identifier,
                    service?.asset.label || '-',
                    FLEET_SERVICE_STATUS_LABELS[service.status],
                    service.configuration?.name || '-',
                    service?.lastMaintenanceDate ? formatOnlyDateFromTs(+service?.lastMaintenanceDate) : '-',
                    service?.lastMaintenanceMileage?.toLocaleString() || '-',
                    service?.nextServiceDate ? formatOnlyDateFromTs(+service?.nextServiceDate) : '-',
                    <FlexRow
                      gap={theme.sizing.scale200}
                      key={`details-${service._id}`}
                    >
                      <StyledTooltip
                        content="Registrar mantenimiento"
                        showArrow={false}
                      >
                        <Button
                          size="mini"
                          shape="square"
                          onClick={() => onOpenServiceJobModal(service)}
                          kind="tertiary"
                        >
                          <MaterialIcon
                            name="car_gear"
                            size={theme.sizing.scale700}
                          />
                        </Button>
                      </StyledTooltip>

                      <StyledTooltip
                        key={`details-${service._id}`}
                        content="Cancelar servicio"
                        showArrow={false}
                      >
                        <Button
                          size="mini"
                          shape="square"
                          onClick={() => onOpenDeleteModal(service)}
                          kind="tertiary"
                        >
                          <MaterialIcon
                            name="cancel"
                            size={theme.sizing.scale700}
                          />
                        </Button>
                      </StyledTooltip>
                    </FlexRow>,
                  ],
                };
              })}
              totalItems={totalItems}
              page={page}
              onPageChange={onChangePage}
              loading={loading}
            />
          )}
          {!services.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      {deleteServiceDisplay}
      <FleetServiceJobModal
        selectedAsset={selectedService?.asset}
        selectedService={selectedService}
        jobType={FleetJobType.maintenance}
        isOpen={showModal}
        onClose={(update) => {
          if (update) onReload();
          onCloseServiceJobModal();
        }}
      />
    </FlexColumn>
  );
}
